import { ServerListViewChangeData } from '@/components/ServerListView/ServerListViewChangeData';
import { URLS } from '@/constants';
import { PaginatedResponse } from '@/models/api';
import { JsonError } from '@/models/jsonApi';
import { LogisticsShipment, TransportOrderRouteDetails, TransportOrderRouteDirections } from '@/models/logistics';
import {
  ASN,
  BrandShort,
  IR11tTransportOrderDetails,
  OutboundQueueOrder,
  OutboundQueueOrderSchedule,
  ReplenishmentQueueOrder,
  TransportCompany,
} from '@/models/warehouse';
import { useUserStore } from '@/piniaStores/user/user.store';
import { apiSlugSession, createSession } from '@/services/session';
import { buildPath } from '@/utils';

/**
 * Create an ASN
 * @param [payload.poNumber] - entered product order number
 * @param [payload.brandId] - the selected brands ID
 * @param [payload.csvFile] - the uploaded CSV file
 */
export function createASN(payload: { poNumber: string; brandId: string; csvFile: Blob }): Promise<JsonError | ASN> {
  const { csvFile, brandId, poNumber } = payload;

  const userStore = useUserStore();

  const params = {
    company_slug: userStore.userActiveCompany?.slug,
  };

  const data = new FormData();

  data.append('po_number', poNumber);
  data.append('brand_id', brandId);
  data.append('line_items', csvFile);

  const axiosSession = createSession(URLS.INTERNAL, { shouldTransform: false });

  return axiosSession.post(
    `${params.company_slug}/${URLS.WAREHOUSE.BASE}/${URLS.WAREHOUSE.ASNS}?${URLS.CUSTOM_ERROR}`,
    data,
    { headers: { 'Content-Type': 'multipart/form-data; charset=utf-8; boundary=" "' } },
  );
}

/**
 * Get a list of outbound queue orders
 * @type {object} params Any parameters for the endpoint.
 */

export const getOutboundQueueOrders = async (payload: ServerListViewChangeData['serialized']) => {
  const params = new URLSearchParams(payload);
  if (!params.has('page')) {
    params.set('page', '1');
  }

  const url = `${URLS.WAREHOUSE.BASE}/${URLS.WAREHOUSE.OUTBOUND_QUEUE}/?${URLS.CUSTOM_ERROR}&${params.toString()}`;
  const response = await apiSlugSession.get<PaginatedResponse<OutboundQueueOrder>>(url);

  return response.data;
};

/**
 * Get Shipments from API
 * @type {object} params Any parameters for the endpoint.
 */
export async function getShipments(params = '') {
  const url = `${URLS.WAREHOUSE.SHIPMENTS}/?${URLS.CUSTOM_ERROR}&${params}`;
  const response = await apiSlugSession.get<PaginatedResponse<LogisticsShipment>>(url);

  return response.data;
}

/**
 * Get a list of all fulfillment queue orders filters
 * @type {object} params Any parameters for the endpoint.
 */
export const getFulfillmentQueueOrdersFilters = async () => {
  const url = `${URLS.WAREHOUSE.BASE}/${URLS.WAREHOUSE.FULFILLMENT_QUEUE}/?${URLS.CUSTOM_ERROR}`;
  const response = await apiSlugSession.get<OutboundQueueOrder[]>(url);

  return response.data;
};

/**
 * Get a list of all fulfillment queue orders with pagination
 * @type {object} params Any parameters for the endpoint.
 */
export const fetchFulfillmentQueueOrdersWithPagination = async (
  payload: ServerListViewChangeData['serialized'],
): Promise<PaginatedResponse<OutboundQueueOrder>> => {
  const params = new URLSearchParams(payload);
  if (!params.has('page')) {
    params.set('page', '1');
  }

  const url = `${URLS.WAREHOUSE.BASE}/${URLS.WAREHOUSE.FULFILLMENT_QUEUE}/?${URLS.CUSTOM_ERROR}&${params.toString()}`;
  const response = await apiSlugSession.get<PaginatedResponse<OutboundQueueOrder>>(url);

  return response.data;
};

/**
 * Schedule outbound order
 * @type {object} payload Any parameters for the endpoint.
 */
export const scheduleOutboundQueueOrder = async (payload: OutboundQueueOrderSchedule) => {
  const url = `${URLS.WAREHOUSE.BASE}/${URLS.WAREHOUSE.OUTBOUND_QUEUE}/?${URLS.CUSTOM_ERROR}`;
  const response = await apiSlugSession.post<OutboundQueueOrder>(url, payload);

  return response.data;
};

/**
 * Get transport order details
 */
export const getTransportOrderRouteDetails = async (payload: { orderNumber: string }) => {
  const url = `${URLS.WAREHOUSE.BASE}/${URLS.WAREHOUSE.FULFILLMENT_QUEUE}/${payload.orderNumber}/${URLS.WAREHOUSE.ROUTE}/?${URLS.CUSTOM_ERROR}`;

  const response = await apiSlugSession.get<TransportOrderRouteDetails>(url);

  return response.data;
};

/**
 * Get transport order directions
 */
export const getTransportOrderRouteDirections = async (payload: { orderNumber: string }) => {
  const url = `${URLS.WAREHOUSE.BASE}/${URLS.WAREHOUSE.FULFILLMENT_QUEUE}/${payload.orderNumber}/${URLS.WAREHOUSE.DIRECTIONS}/?${URLS.CUSTOM_ERROR}`;

  const response = await apiSlugSession.get<TransportOrderRouteDirections>(url);

  return response.data;
};

/**
 * Regenerate Metrc transfer template for a Transport Order
 */
export const regenerateMetrcTransferTemplate = async (payload: { orderNumber: string }) => {
  const url = `${URLS.WAREHOUSE.BASE}/${URLS.WAREHOUSE.FULFILLMENT_QUEUE}/${payload.orderNumber}/${URLS.LOGISTICS.TRANSFER}/?${URLS.CUSTOM_ERROR}`;

  await apiSlugSession.post(url);
};

/**
 * Get warehouse seller companies
 */
export const getSellerCompanies = async () => {
  const url = `${URLS.WAREHOUSE.BASE}/${URLS.WAREHOUSE.SELLERS}/?${URLS.CUSTOM_ERROR}&limit=10000`;

  const response = await apiSlugSession.get(url);

  return response.data;
};

export const getValidTransportCompanies = async () => {
  const url = `${URLS.WAREHOUSE.BASE}/${URLS.WAREHOUSE.FULFILLMENT_QUEUE}/${URLS.WAREHOUSE.ELIGIBLE_TRANSPORT_COMPANIES}`;
  const response = await apiSlugSession.get<TransportCompany[]>(url);

  return response.data;
};

/**
 * Schedule outbound order
 * @type {object} payload Any parameters for the endpoint.
 */
export const updateFulfillmentQueueOrder = async (payload: {
  orderNumber: string;
  requestedDeliveryDate: string;
}): Promise<OutboundQueueOrder> => {
  const response = await apiSlugSession.post(
    `${URLS.WAREHOUSE.BASE}/${URLS.WAREHOUSE.FULFILLMENT_QUEUE}/?${URLS.CUSTOM_ERROR}`,
    payload,
  );

  return response.data;
};

/**
 * Get a list of all replenishment queue order shipments
 * @return a list of ReplenishmentQueueOrder objects
 */
export const getReplenishmentQueueOrderShipments = async () => {
  const url = `${URLS.REPLENISHMENT.SHIPMENTS}?${URLS.CUSTOM_ERROR}`;
  const response = await apiSlugSession.get<{ results: { shipments: ReplenishmentQueueOrder[] } }>(url);

  return response.data;
};

/**
 * Regenerate Metrc transfer template for a Replenishment Shipment Order
 */
export const regenerateR11tShipmentMetrcTransferTemplate = async (payload: { orderNumber: string | number }) => {
  const url = `${URLS.REPLENISHMENT.SHIPMENTS}/${payload.orderNumber}/${URLS.LOGISTICS.REPROCESS}?${URLS.CUSTOM_ERROR}`;

  await apiSlugSession.post(url);
};

/**
 * Get Replenishment queue transport order details
 */
export const getR11tTransportOrderDetails = async (payload: { orderId: string | number }) => {
  const params = {
    error_format: 'jsonapi',
  };

  const url = `${URLS.REPLENISHMENT.SHIPMENTS}/${payload.orderId}`;

  const response = await apiSlugSession.get<{ results: IR11tTransportOrderDetails }>(url, { params });

  return response.data.results.orderDetails;
};

/**
 * Get Replenishment queue transport order route details and directions
 */
export const getR11tTransportOrderRouteDetails = async (payload: {
  orderId: string | number;
  getDirections?: boolean;
}) => {
  const params = {
    error_format: 'jsonapi',
  };

  let detailFlag;

  if (!payload.getDirections) {
    detailFlag = 'routeDetails';
  } else {
    detailFlag = 'directions';
    params['should_get_directions'] = true;
  }

  const url = `${URLS.REPLENISHMENT.SHIPMENTS}/${payload.orderId}`;

  const response = await apiSlugSession.get<{ results: IR11tTransportOrderDetails }>(url, { params });

  return response.data.results[detailFlag];
};

/**
 * Fetch Warehouse imported ASNs Brands
 */
export async function getBrands() {
  const url = `${URLS.WAREHOUSE.BASE}/${URLS.WAREHOUSE.BRANDS}/?${URLS.CUSTOM_ERROR}`;

  const response = await apiSlugSession.get<BrandShort[]>(url);

  return response.data;
}

/**
 * Get Retail Fulfillment Customer Warehouse Details
 */
export const getRetailFulfillmentWarehouseDetails = async ({ customerId }) => {
  const path = buildPath(URLS.CUSTOMER.WAREHOUSE_DETAIL, { customerId });
  const url = `${path}?${URLS.CUSTOM_ERROR}`;

  const response = await apiSlugSession.get(url);

  return response.data;
};
